import a11yIIBScreenReaderService, {
    A11yForIIBScreenReaderIteration,
} from '../../../../services/a11yIIBScreenReaderService';

/**
 * Determines the aria-label value for math input fields in screen reader contexts.
 * Returns a zero-width space character when a11y v3 is supported to suppress unwanted announcements,
 * or undefined otherwise to allow default screen reader behavior.
 *
 * @param {boolean} isMathInput - Whether the input is a math input component
 * @returns {string|undefined} Zero-width space character or undefined
 */
export const getAriaLabelForMathInput = (isMathInput) => {
    if (!isMathInput) {
        return undefined;
    }

    const isA11yIIBScreenReaderSupported = a11yIIBScreenReaderService
        .isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3);

    // Zero-width space (\u200B) is recognized as valid aria-label while reading as silence,
    // preventing unwanted "Symbols menu" announcements
    return isA11yIIBScreenReaderSupported ? '\u200B' : undefined;
};

/**
 * Determines whether the MathEntryInput component should update based on prop/state changes.
 * Only triggers updates for:
 * - isReadOnly prop changes
 * - isTooWide state changes
 * - isFocused state changes (only when a11y v3 is supported)
 *
 * @param {object} currentProps - Current component props
 * @param {object} nextProps - Next component props
 * @param {object} currentState - Current component state
 * @param {object} nextState - Next component state
 * @returns {boolean} Whether the component should update
 */
export const shouldMathEntryInputUpdate = (currentProps, nextProps, currentState, nextState) => {
    const isA11yV3Supported = a11yIIBScreenReaderService
        .isA11yIterationForIIBScreenReaderSupported(A11yForIIBScreenReaderIteration.v3);

    return currentProps.isReadOnly !== nextProps.isReadOnly
        || currentState.isTooWide !== nextState.isTooWide
        || (isA11yV3Supported && currentState.isFocused !== nextState.isFocused);
};
